
import { defineComponent, ref, reactive, onMounted, computed, watch } from 'vue'
import { stockpool } from '@/api/index'
import { useRoute } from 'vue-router'
import moment from 'moment'
import WebSocket from '@/components/websocket2.vue'
import ProductCard from './components/ProductCard.vue'
import TabBar from './components/TabBar.vue'
import FilterPanel from './components/FilterPanel.vue'
import PoolTable from './components/PoolTable.vue'
import AiInterpModal from './components/AiInterpModal.vue'
import { getCourseConfig, getSortFieldMap, getWsListKey } from './components/courseConfig'
import { formatChipMasterUpdateTime } from './components/chipMasterUtils'
import { formatMengzhuUpdateTime } from './components/mengzhuTrendUtils'

export default defineComponent({
  components: {
    WebSocket,
    ProductCard,
    TabBar,
    FilterPanel,
    PoolTable,
    AiInterpModal
  },
  setup() {
    const route = useRoute()

    // 主题：dark �?light
    // isModalTheme=true 时强制使�?light，否则根�?theme 参数判断
    const isModalTheme = String(route.query.isModalTheme) === 'true'
    const theme = ref(isModalTheme ? 'light' : ((route.query.theme as string) || 'light'))
    console.log('isModalTheme:', route.query.isModalTheme, 'result:', isModalTheme, 'theme:', theme.value)
    // 当前课程编号，默认四维猎�?
    const currentCourseNo = ref((route.query.courseNo as string) || 'e4408ecedc054856946546a185e8c047')

    // isClick: 1=弹窗模式(只有双击) 2=内嵌模式(单击+双击)，默�?
    const isClick = ref(Number(route.query.isClick) || 2)

    // 获取当前课程配置
    const currentCourseConfig = ref(getCourseConfig(currentCourseNo.value))
    const currentWsListKey = getWsListKey(currentCourseNo.value)

    // 判断是否为筹码大�?
    const isChipMaster = computed(() => currentCourseNo.value === '17d1f0b0296e4b40ada792c7aa1599ec')

    // 判断是否为盟主趋�?
    const isMengzhuTrend = computed(() => currentCourseNo.value === '399a9139a99a4300a9bf004b9949eb9f')

    // 判断是否为龙头信�?
    const isDragonSignal = computed(() => currentCourseNo.value === 'b20f2d6e654948ae8c99bf650cbbb0e2')

    // 判断是否为龙头猎�?
    const isDragonHunter = computed(() => currentCourseNo.value === '03fc903a5ac84ff6982e4b83a1f35c61')

    // 盟主趋势/龙头信号顶级 Tab 状�?
    // 盟主趋势�?signalPool' �?'hotCases'
    // 龙头信号�?dragon_signal_sprint' �?'dragon_signal_ignition'
    const mengzhuMainTab = ref(isDragonSignal.value ? 'dragon_signal_sprint' : 'signalPool')

    // 龙头猎手一�?Tab：newChose / dataTrack / hotCase / beforeChose
    const dragonHunterMainTab = ref('newChose')
    // 龙头猎手二级 Tab（信号类型）：dragonStar / dragonDouble / overTaking / dragonLeft / dragonRight
    const dragonHunterSubTab = ref('dragonStar')
    // 龙头猎手二级 tab 对应�?type 参数（最新入选接口用数字�?
    const DRAGON_HUNTER_TYPE_MAP: Record<string, number> = {
      dragonStar: 1, dragonDouble: 2, overTaking: 3, dragonLeft: 4, dragonRight: 5
    }
    // 往期入选：只看自�?
    const dragonHunterOnlyUser = ref(0)

    // 切换 Tab 锁，防止由于 Table 内部组件重置导致的排序状态错误保�?
    const isSwitchingTab = ref(false)

    // 全局独立排序状态存储，确保一二级菜单组合完全隔离
    // 键格式：`courseNo_mainTab_subTab`
    const allSortStates = reactive<Record<string, { sortField: string; sortType: string }>>({})

    // 获取当前视角的唯一排序�?
    function getCurrentSortKey(tab?: string, sub?: string) {
      const main = tab || (isDragonHunter.value ? dragonHunterMainTab.value : mengzhuMainTab.value)
      const subTab = sub || (isDragonHunter.value ? dragonHunterSubTab.value : signalType.value)
      return `${currentCourseNo.value}_${main}_${subTab}`
    }

    // 往期入选：搜索股票
    const dragonHunterSearchSymbol = ref('')
    const dragonHunterSearchMarket = ref('')
    // 往期入选：当前选中的股票（用于回填搜索框）
    const dragonHunterSelectedStock = ref<any>(null)

    // ==================== 计算属性：简化模板绑�?====================

    // 是否有产品数�?
    const hasProductData = computed(() => productData.value && productData.value.length > 0)

    // 获取产品卡片�?props（提取公共属性）
    const getProductCardProps = (product: any) => ({
      courseNo: product.courseNo,
      poolName: product.poolName,
      poolNameImg: product.poolNameImg,
      poolNameImgDark: product.poolNameImgDark,
      remark: product.remark,
      tags: product.tags,
      content: product.content,
      disclaimer: product.disclaimer,
      poolIcon: product.poolIcon
    })

    // FilterPanel 组件�?props（统一管理筛选面板配置）
    const filterPanelProps = computed(() => ({
      // 基础数据
      updateTime: updateTime.value,
      historyDates: historyDates.value,
      theme: theme.value,
      // 功能开关（根据不同产品配置�?
      showFilterBtn: currentCourseConfig.value.showFilter !== false,           // 是否显示筛选按�?
      showStockCount: currentCourseConfig.value.showStockCount === true,       // 是否显示股票数量
      stockCount: totalCount.value,
      showStockSearch: currentCourseConfig.value.showStockSearch === true,     // 是否显示股票搜索（筹码大师）
      showSelectedStockInfo: currentCourseConfig.value.showSelectedStockInfo === true, // 是否显示已选股票信�?
      selectedStockCount: totalCount.value,
      selectedStockDescription: currentCourseConfig.value.selectedStockDescription || '',
      // 信号标签配置（四维猎手/箱体擒龙/筹码大师/盟主趋势各有不同）
      signalTabs: currentCourseConfig.value.signalTabs || { latest: '最新信号', history: '历史信号' },
      showHistoryDateSelect: !isChipMaster.value,  // 筹码大师不显示历史日期选择，盟主趋势信号池显示
      // 盟主趋势特有配置
      showOptionalFilter: currentCourseConfig.value.showOptionalFilter === true,
      mainTabs: currentCourseConfig.value.mainTabs,
      currentMainTab: mengzhuMainTab.value,
      isMengzhuTrend: isMengzhuTrend.value,
      isDragonSignal: isDragonSignal.value,
      // 龙头猎手特有配置
      isDragonHunter: isDragonHunter.value,
      dragonHunterMainTab: dragonHunterMainTab.value,
      dragonHunterSubTab: dragonHunterSubTab.value,
      dragonHunterSelectedStock: dragonHunterSelectedStock.value
    }))

    // PoolTable 组件�?props（统一管理表格配置�?
    const poolTableProps = computed(() => ({
      dataSource: poolList.value,
      loading: loading.value,
      hasMore: hasMore.value,
      loadingMore: loadingMore.value,
      theme: theme.value,
      courseNo: currentCourseNo.value,
      productData: productData.value,
      isClick: isClick.value,
      defaultSortField: 'pxChangRate',   // 默认按涨幅排�?
      defaultSortOrder: 'descend',       // 默认降序
      // 受控排序状态：将父组件维护的当前排序同步给子组件表格显�?
      sortFieldProp: filterParams.sortField,
      sortOrderProp: filterParams.sortType,
      mengzhuMainTab: mengzhuMainTab.value,  // 盟主趋势/龙头信号顶级 Tab
      // 盟主趋势：用于判断最高涨幅和累计涨幅的显示逻辑
      mengzhuLastTradingDay: mengzhuLastTradingDay.value,
      mengzhuUpdateTime: updateTime.value,
      mengzhuSignalType: signalType.value,
      // 龙头信号：回测数据跟�?
      isDragonSignal: isDragonSignal.value,
      dragonSignalStrategyCode: mengzhuMainTab.value,  // 当前龙头信号策略�?
      // 龙头猎手
      isDragonHunter: isDragonHunter.value,
      dragonHunterMainTab: dragonHunterMainTab.value,
      dragonHunterSubTab: dragonHunterSubTab.value
    }))

    // 筹码大师交易日和更新时间
    const chipMasterTradingDay = ref<number>(0)
    const chipMasterUpdateTime = ref<number>(0)

    // 产品数据 - 从远程接口获�?
    const productData = ref<any[]>([])

    // 获取产品数据（根据当�?courseNo 配置化筛选）
    async function fetchProductData() {
      try {
        const res = await stockpool.getProductConfig()
        console.log('fetchProductData res:', res)

        // 兼容不同的数据结构：res.data �?res 本身
        const dataList = res.data

        if (dataList && Array.isArray(dataList)) {
          // 根据当前 courseNo 筛选对应的产品数据
          const filteredData = dataList.filter((item: any) => item.courseNo === currentCourseNo.value)
          console.log('fetchProductData filteredData:', filteredData, 'courseNo:', currentCourseNo.value)
          productData.value = filteredData
        } else {
          console.warn('fetchProductData: 数据格式不正确', dataList)
          productData.value = []
        }
      } catch (error) {
        console.error('fetchProductData failed:', error)
        productData.value = []
      }
    }

    const activeTab = ref(0)

    // PoolTable 组件引用
    const poolTableRef = ref<any>(null)
    const aiInterpModalRef = ref<any>(null)

    // 四维猎手表格数据
    const poolList = ref<any[]>([])
    // 可视区域内的股票列表（用�?WebSocket 订阅优化，只订阅可视区域内的股票�?
    const visiblePoolList = ref<any[]>([])
    const loading = ref(false)
    const updateTime = ref('')
    const historyDates = ref<string[]>([])
    // 滚动加载状�?
    const hasMore = ref(true)
    const loadingMore = ref(false)
    const totalCount = ref(0)

    // 盟主趋势：最后一个有效交易日（用于判断最高涨幅和累计涨幅的显示逻辑�?
    const mengzhuLastTradingDay = ref('')

    // 请求序列号，用于处理快速点击排序时的竞态条�?
    // 只有最新请求的响应才会被处理，旧请求的响应会被忽略
    let requestSequence = 0

    // 盟主趋势 Tab 切换防抖定时�?
    let mainTabChangeTimer: ReturnType<typeof setTimeout> | null = null
    const MAIN_TAB_CHANGE_DELAY = 300 // 防抖延迟时间（毫秒）

    // 盟主趋势信号类型切换防抖定时�?
    let signalTypeChangeTimer: ReturnType<typeof setTimeout> | null = null
    const SIGNAL_TYPE_CHANGE_DELAY = 300 // 防抖延迟时间（毫秒）

    // 筛选参�?
    const filterParams = reactive({
      tradeDay: '',
      onlyOptional: 0,
      userName: '',
      isChose: 0,
      qs: 0,        // 趋势筛选：0:全部 1:强势 2:弱势
      zj: 0,        // 资金筛选：0:全部 1:强势 2:弱势
      tc: '',       // 题材筛选：0:全部�?:核心风口 2:普通风�?3:未入选，多选用逗号拼接
      sortField: '',
      sortType: '',
      pageNo: 1,
      pageSize: 100
    })

    // 信号类型：latest �?history
    const signalType = ref('latest')

    function fresh() {
      handleRefresh()
    }

    // 获取交易日（使用通用�?getTradingDays API�?
    const fetchTradingDays = async (time: number) => {

      console.log('fetchTradingDays 开始执�? time:', time)

      // 使用通用的交易日API
      const tokenRes = await stockpool.getToken()
      console.log('getToken 返回:', tokenRes.data.Data)

      const token = tokenRes.data.Data?.Token
      if (!token) {
        console.error('获取 Token 失败，返回数�?', tokenRes.data)
        return
      }

      const market = route.query.market || route.query.marketid || 'sz'

      // �?productData 中获�?historyDays，如果没有则默认�?10
      const historyDays = productData.value?.[0]?.historyDays || 11

      // 多请求一个交易日，因为后续会过滤�?updateTime 当天的日�?
      const params = {
        market: market,
        limit: Number(historyDays) + 2,
        isafter: 0,
      }

      console.log('Fetching trading days with params:', params, 'token:', token, 'historyDays:', productData.value?.[0])

      // 根据配置获取交易日API名称并调�?
      const tradingDaysApiName = currentCourseConfig.value.tradingDaysApi || 'getTradingDays'
      const tradingDaysApiFn = (stockpool as any)[tradingDaysApiName]

      if (typeof tradingDaysApiFn !== 'function') {
        console.error(`API ${tradingDaysApiName} not found in stockpool`)
        return
      }

      const daysRes = await tradingDaysApiFn.call(stockpool, params, token)
      console.log('daysRes:', daysRes)

      // 更新 historyDates - 将时间戳转换为日期格�?
      if (daysRes.data && Array.isArray(daysRes.data.data)) {
        const allDates = daysRes.data.data.map((timestamp: number) => {
          return moment(timestamp * 1000).format('YYYY-MM-DD')
        })

        // 盟主趋势：根�?updateTime 过滤日期，历史信号只显示更新时间之前的日�?
        if (isMengzhuTrend.value) {
          // 保存最后一个有效交易日（交易日列表的第一个，即最新的交易日）
          if (allDates.length > 0) {
            mengzhuLastTradingDay.value = allDates[0]
          }

          // 获取 updateTime 的日期（只取日期部分，不含时间）
          const updateTimeDate = updateTime.value ? moment(updateTime.value).format('YYYY-MM-DD') : ''

          // 过滤�?updateTime 当天及之后的日期，只保留 updateTime 之前的日�?
          // 使用 <= 比较确保当天也被过滤�?
          let filteredDates = allDates
          if (updateTimeDate) {
            filteredDates = allDates.filter((date: string) => date < updateTimeDate)
          } else {
            // 如果没有 updateTime，使用当前日期过�?
            const todayDate = moment().format('YYYY-MM-DD')
            filteredDates = allDates.filter((date: string) => date < todayDate)
          }

          // 如果过滤后没有数据，使用原始数据（去掉第一个）
          if (filteredDates.length === 0 && allDates.length > 0) {
            filteredDates = allDates.slice(1)
          }

          // 如果还是没有数据，直接使用所有日�?
          if (filteredDates.length === 0) {
            filteredDates = allDates
          }

          // 截取最�?historyDays 个数据（因为多请求了一个用于补偿过滤）
          historyDates.value = filteredDates.slice(0, historyDays == '11' ? 10 : historyDays)
        } else {
          // 其他产品：根�?updateTime 过滤日期
          // 获取 updateTime 的日�?
          const updateTimeDate = updateTime.value ? moment(updateTime.value).format('YYYY-MM-DD') : ''

          // 过滤�?updateTime 当天及之后的日期，只保留 updateTime 之前的日�?
          // 如果 updateTime 为空，则不过滤，直接使用所有日�?
          let filteredDates = allDates
          if (updateTimeDate) {
            filteredDates = allDates.filter((date: string) => date < updateTimeDate)
          }

          // 如果过滤后没有数据，使用原始数据（去掉第一个，因为第一个通常是当天）
          if (filteredDates.length === 0 && allDates.length > 0) {
            filteredDates = allDates.slice(1)
          }

          // 如果还是没有数据，直接使用所有日�?
          if (filteredDates.length === 0) {
            filteredDates = allDates
          }

          // 截取最�?historyDays 个数据（因为多请求了一个用于补偿过滤）
          historyDates.value = filteredDates.slice(0, historyDays == '11' ? 10 : historyDays)
          console.log('historyDates 更新�?', historyDates.value, 'updateTimeDate:', updateTimeDate, 'allDates:', allDates, 'historyDays:', historyDays)
        }
      }
    }



    // 获取股池数据（根据配置动态调用API�?
    function getPoolData(isLoadMore = false) {
      // 增加请求序列号，用于处理快速点击排序时的竞态条�?
      requestSequence++
      const currentSequence = requestSequence

      if (isLoadMore) {
        loadingMore.value = true
        isLoadingMore = true  // 标记当前为滑动加载，�?watch(poolList) 判断
      } else {
        loading.value = true
        isLoadingMore = false
      }

      // 根据不同产品构建不同的请求参�?
      const isBoxDragon = currentCourseNo.value === '33130259066e4650bd0b7cf6b23d9488'
      const isChipMasterProduct = currentCourseNo.value === '17d1f0b0296e4b40ada792c7aa1599ec'
      const isMengzhuTrendProduct = currentCourseNo.value === '399a9139a99a4300a9bf004b9949eb9f'
      const isDragonSignalProduct = currentCourseNo.value === 'b20f2d6e654948ae8c99bf650cbbb0e2'
      const isDragonHunterProduct = currentCourseNo.value === '03fc903a5ac84ff6982e4b83a1f35c61'

      // filterParams.sortField 存的是前端列 key，发请求前通过 fieldMap 转成接口字段�?
      const fieldMap = getSortFieldMap(currentCourseNo.value)
      const apiSortField = filterParams.sortField ? (fieldMap[filterParams.sortField] || filterParams.sortField) : ''

      let queryData: any = {}

      if (isMengzhuTrendProduct) {
        // 盟主趋势使用特定的参数结构（与四维猎手类似）
        queryData = {
          onlyOptional: filterParams.onlyOptional,
          sortField: apiSortField || 'PxChangeRate',
          sortType: filterParams.sortType || 'desc',
          pageNo: filterParams.pageNo,
          pageSize: filterParams.pageSize
        }
        // 历史信号模式下传�?tradeDay
        if (filterParams.tradeDay) {
          queryData.tradeDay = filterParams.tradeDay
        }
        // 添加 userName 用于自选股功能
        if (filterParams.onlyOptional === 1) {
          // queryData.tradeDay = filterParams.userName || route.query.userName || ''
        }
      } else if (isChipMasterProduct) {
        // 筹码大师使用特定的参数结�?
        const signalTypeMap = currentCourseConfig.value.signalTypeMap || { latest: 'upBreak', history: 'strongSupport' }
        queryData = {
          trendType: signalTypeMap[signalType.value as 'latest' | 'history'] || 'upBreak',
          optional: filterParams.onlyOptional,
          pageNo: filterParams.pageNo,
          pageSize: filterParams.pageSize,
          sortField: apiSortField || 'pxChangeRate',
          sortType: filterParams.sortType || 'desc'
        }
        // 添加 userName 用于自选股功能
        if (filterParams.onlyOptional === 1) {
          queryData.userName = filterParams.userName || route.query.userName || ''
        }
      } else if (isBoxDragon) {
        // 箱体擒龙使用不同的参数结�?
        queryData = {
          sortField: apiSortField,
          sortType: filterParams.sortType,
          pageNo: filterParams.pageNo,
          pageSize: filterParams.pageSize,
          onlyOptional: filterParams.onlyOptional,
          strategyCode: 'great_wisdom_bs' // 箱体擒龙策略代码
        }
        if (filterParams.tradeDay) {
          queryData.tradingDay = filterParams.tradeDay
        }
        // 添加 userName 用于自选股功能
        if (filterParams.onlyOptional === 1) {
          queryData.userName = filterParams.userName || route.query.userName || ''
        }
      } else if (isDragonSignalProduct) {
        // 龙头信号使用 ladder/list 接口，strategyCode 区分龙头冲刺/龙头点火
        // 接口参数名：sortField（小写）、SortType（大写S，数字：1=降序�?=升序�?
        const sortTypeNum = filterParams.sortType === 'asc' ? 2 : (filterParams.sortType === 'desc' ? 1 : 1)
        queryData = {
          sortField: apiSortField,
          SortType: sortTypeNum,
          pageNo: filterParams.pageNo,
          pageSize: filterParams.pageSize,
          onlyOptional: filterParams.onlyOptional,
          strategyCode: mengzhuMainTab.value // dragon_signal_sprint �?dragon_signal_ignition
        }
        if (filterParams.tradeDay) {
          queryData.tradingDay = filterParams.tradeDay
        }
        if (filterParams.onlyOptional === 1) {
          queryData.userName = filterParams.userName || route.query.userName || ''
        }
      } else if (isDragonHunterProduct) {
        // 龙头猎手：根据一�?tab 决定请求参数
        if (dragonHunterMainTab.value === 'newChose') {
          queryData = { type: DRAGON_HUNTER_TYPE_MAP[dragonHunterSubTab.value] || 1 }
        } else if (dragonHunterMainTab.value === 'hotCase') {
          queryData = {}
        } else if (dragonHunterMainTab.value === 'beforeChose') {
          queryData = {
            isUser: dragonHunterOnlyUser.value,
            pageNo: filterParams.pageNo,
            pageSize: filterParams.pageSize,
            sortField: apiSortField || 'feed_timestamp',
            sortType: filterParams.sortType || 'desc',
            symbol: dragonHunterSearchSymbol.value || '',
            market: dragonHunterSearchMarket.value || ''
          }
        }
      } else {
        // 四维猎手等其他产品使用原有参数结�?
        queryData = {
          tradeDay: filterParams.tradeDay,
          onlyOptional: filterParams.onlyOptional,
          userName: filterParams.userName || route.query.userName || '',
          isChose: filterParams.isChose,
          qs: filterParams.qs,
          zj: filterParams.zj,
          tc: filterParams.tc,
          sortField: apiSortField,
          sortType: filterParams.sortType,
          pageNo: filterParams.pageNo,
          pageSize: filterParams.pageSize
        }
        if (filterParams.tradeDay) {
          queryData.tradeDay = filterParams.tradeDay
        }
      }

      const data: any = {}
      data.data = queryData
      data.headers = {
        clienttype: 'pc',
        appVersion: route.query.appVersion,
        token: route.query.token,
        "Content-Type": "application/json"
      }

      // 根据配置获取API名称并调�?
      // 龙头猎手根据一�?tab 动态选择 API
      let apiName = currentCourseConfig.value.requestApi
      if (isDragonHunterProduct) {
        if (dragonHunterMainTab.value === 'hotCase') apiName = 'getDragonHunterHotCase'
        else if (dragonHunterMainTab.value === 'beforeChose') apiName = 'getDragonHunterBeforeChose'
        else apiName = 'getDragonHunterNewChose'
      }
      const apiFn = (stockpool as any)[apiName]

      if (typeof apiFn !== 'function') {
        console.error(`API ${apiName} not found in stockpool`)
        loading.value = false
        loadingMore.value = false
        return
      }

      apiFn.call(stockpool, data).then((res: any) => {
        // 检查请求序列号，如果不是最新请求则忽略响应（解决快速点击排序时的竞态条件）
        if (currentSequence !== requestSequence) {
          console.log(`[排序] 忽略旧请求响应，当前序列�? ${requestSequence}，响应序列号: ${currentSequence}`)
          return
        }

        // 盟主趋势：检查当�?Tab 是否仍然�?signalPool，避免数据错�?
        if (isMengzhuTrendProduct && mengzhuMainTab.value !== 'signalPool') {
          console.log(`[盟主趋势] 当前 Tab 已切换为 ${mengzhuMainTab.value}，忽略信号池响应`)
          loading.value = false
          loadingMore.value = false
          return
        }

        if (res.data) {
          let newList: any[] = []

          if (isMengzhuTrendProduct) {
            // 盟主趋势数据字段映射
            const rawList = res.data.poolList || []
            newList = rawList.map((item: any) => ({
              ...item,
              // 字段映射：API字段 -> 前端字段
              pxChangRate: item.pxChangeRate, // 涨幅
              plateName: item.plate?.name || '' // 题材板块名称
            }))
            totalCount.value = res.data.total || rawList.length || 0

            // 盟主趋势更新时间格式化（与四维猎手相同）
            if (res.data.updateTime) {
              updateTime.value = formatMengzhuUpdateTime(res.data.updateTime)
              // 只有在最新信号模式下才更新交易日列表，历史信号模式下不更�?
              // 这样可以确保历史日期下拉框的内容与更新时间匹�?
              if (signalType.value === 'latest') {
                const timestamp = res.data.updateTime > 9999999999
                  ? Math.floor(res.data.updateTime / 1000)
                  : res.data.updateTime
                fetchTradingDays(timestamp)
              }
            }
          } else if (isChipMasterProduct) {
            // 筹码大师数据字段映射
            const rawList = res.data.list || []
            newList = rawList.map((item: any) => ({
              ...item,
              // 字段映射：API字段 -> 前端字段
              pxChangRate: item.pxChangeRate, // 涨幅
            }))
            totalCount.value = res.data.total || rawList.length || 0

            // 保存交易日和更新时间用于格式�?
            chipMasterTradingDay.value = res.data.tradingDay || 0
            chipMasterUpdateTime.value = res.data.updateTime || 0

            // 筹码大师更新时间格式�?
            if (res.data.updateTime) {
              updateTime.value = formatChipMasterUpdateTime(res.data.updateTime, res.data.tradingDay)
            }
          } else if (isBoxDragon) {
            // 箱体擒龙数据字段映射
            const rawList = res.data.list || []
            // 创建现有数据的映射，用于保留 WebSocket 更新的字�?
            const existingDataMap = new Map<string, any>()
            if (poolList.value && poolList.value.length > 0) {
              poolList.value.forEach((item: any) => {
                if (item && item.symbol) {
                  existingDataMap.set(item.symbol, item)
                }
              }) 
            }
            newList = rawList.map((item: any) => {
              const existingItem = existingDataMap.get(item.symbol)
              return {
                ...item,
                // 字段映射：API字段 -> 前端字段
                pxChangRate: item.pxChangeRate, // 涨幅
                highPct: item.highPct,          // 最大涨�?
                businessBalance: item.businessBalance, // 金额
                turnoverRatio: item.turnoverRatio,     // 换手
                // 总量：优先使�?API 返回的非零值，否则保留已有�?WebSocket 更新�?
                volume: (item.volume && item.volume !== 0) ? item.volume : (existingItem?.volume || 0)
              }
            })
            // 箱体擒龙�?total 可能�?count 字段
            totalCount.value = res.data.count || rawList.length || 0
          } else if (isDragonSignalProduct) {
            // 龙头信号数据字段映射（与箱体擒龙接口相同�?
            const rawList = res.data.list || []
            // 打印第一条原始数据，用于确认接口返回的个股热度字段名
            if (rawList.length > 0) {
              console.log('[龙头信号] 接口返回第一条原始数�?', JSON.stringify(rawList[0], null, 2))
            }
            newList = rawList.map((item: any) => ({
              ...item,
              pxChangRate: item.pxChangeRate, // 涨幅（统一字段名）
              dragonPlateName: item.ext?.plateName || item.plateName || '', // 所属板�?
              // 个股热度字段兼容：接口可能返�?stockHot / stock_hot / hot 等不同字段名
              stockHot: item.stockHot ?? item.stock_hot ?? item.hot ?? item.symbolHot ?? null
            }))
            totalCount.value = res.data.count || rawList.length || 0
          } else if (isDragonHunterProduct) {
            // 龙头猎手数据映射
            if (dragonHunterMainTab.value === 'newChose') {
              const rawList = res.data?.newSource || []
              newList = rawList.map((item: any) => ({
                ...item,
                lastPx: item.lastPrice,
                pxChangRate: item.pxChangeRate
              }))
              totalCount.value = rawList.length
              if (res.data?.dateTime) {
                updateTime.value = moment(res.data.dateTime).format('YYYY-MM-DD')
              }
            } else if (dragonHunterMainTab.value === 'hotCase') {
              const rawList = Array.isArray(res.data) ? res.data : (res.data?.list || [])
              newList = rawList.map((item: any) => ({
                ...item,
                pxChangRate: item.pxChangeRate || 0
              }))
              totalCount.value = rawList.length
            } else if (dragonHunterMainTab.value === 'beforeChose') {
              const rawList = res.data?.selectList || []
              newList = rawList.map((item: any) => {
                // Bug3修复：标准化 market 字段，确�?WebSocket 订阅时市场代码格式正�?
                let market = (item.market || item.Market || '').toLowerCase()
                if (market === 'sse' || market === 'sha' || market === 'shetf') market = 'sh'
                else if (market === 'szse' || market === 'sza' || market === 'szetf') market = 'sz'
                else if (market === 'bse') market = 'bj'
                // 若接口未返回 market，根据股票代码推�?
                if (!market && item.symbol) {
                  const code = String(item.symbol)
                  if (code.startsWith('6') || code.startsWith('5')) market = 'sh'
                  else if (code.startsWith('0') || code.startsWith('3') || code.startsWith('1')) market = 'sz'
                  else if (code.startsWith('4') || code.startsWith('8')) market = 'bj'
                }
                return {
                  ...item,
                  market,
                  // Bug3修复：lastPx 字段映射（接口返�?lastPrice�?
                  lastPx: item.lastPx ?? item.lastPrice ?? 0,
                  pxChangRate: item.pxChangeRate || 0
                }
              })
              totalCount.value = res.data?.total || rawList.length
            }
          } else {
            // 四维猎手等其他产品使用原有数据结�?
            newList = res.data.poolList || []
            totalCount.value = res.data.total || 0
          }

          if (isLoadMore) {
            // 滚动加载时进行去重，避免出现重复股票
            const uniqueNewList = deduplicateStocks(poolList.value, newList)
            // 如果去重后没有新数据，说明已经没有更多数据了（防止无限加载）
            if (uniqueNewList.length === 0 && newList.length > 0) {
              hasMore.value = false
            } else {
              poolList.value = poolList.value.concat(uniqueNewList)
              // 如果返回的数据少于请求的 pageSize，说明已经没有更多数据了
              hasMore.value = newList.length >= filterParams.pageSize && poolList.value.length < totalCount.value
            }
          } else {
            // 首次加载也进行去重，去除 API 返回数据中的内部重复项
            poolList.value = deduplicateStocks([], newList)
            hasMore.value = poolList.value.length < totalCount.value
            // 热门案例：数据加载完成后按默认排序（5日区间最高涨幅降序）做一次本地排序
            if (isDragonHunterProduct && dragonHunterMainTab.value === 'hotCase' && filterParams.sortField) {
              sortPoolList()
            }
          }

          console.log('res.data.total:', totalCount.value, 'loaded:', poolList.value.length)

          // 处理更新时间（非筹码大师、非盟主趋势�?
          // 盟主趋势的更新时间已在上面单独处�?
          if (!isChipMasterProduct && !isMengzhuTrendProduct) {
            if (isBoxDragon || isDragonSignalProduct) {
              // 箱体擒龙/龙头信号：使�?tradingDay 字段显示更新日期
              const resTradingDay = res.data.tradingDay
              // 龙头信号使用 tradingDay 显示更新时间
              if (resTradingDay) {
                updateTime.value = moment(resTradingDay).format('YYYY-MM-DD')
              }
              if (resTradingDay && signalType.value === 'latest') {
                const timestamp = resTradingDay > 9999999999
                  ? Math.floor(resTradingDay / 1000)
                  : resTradingDay
                fetchTradingDays(timestamp)
              } else if (!resTradingDay) {
                console.warn('getPoolData: tradingDay is missing')
              }
            } else {
              // 四维猎手等其他产品：使用 updateTime 字段
              const resUpdateTime = res.data.updateTime
              if (resUpdateTime) {
                updateTime.value = moment(resUpdateTime).format('YYYY-MM-DD HH:mm:ss')
                // 只有在最新信号模式下才更新交易日列表，历史信号模式下不更�?
                if (signalType.value === 'latest') {
                  const timestamp = resUpdateTime > 9999999999
                    ? Math.floor(resUpdateTime / 1000)
                    : resUpdateTime
                  fetchTradingDays(timestamp)
                }
              } else {
                console.warn('getPoolData: updateTime is missing')
              }
            }
          }
        }
        // 龙头猎手 newChose 数据加载完成后，恢复 WebSocket 订阅�?watch(poolList) 统一处理
        loading.value = false
        loadingMore.value = false
        isLoadingMore = false  // 重置滑动加载标志
      }).catch((err: any) => {
        // 检查请求序列号，如果不是最新请求则忽略错误处理
        if (currentSequence !== requestSequence) {
          console.log(`[排序] 忽略旧请求错误，当前序列�? ${requestSequence}，响应序列号: ${currentSequence}`)
          return
        }
        console.error(`${apiName} failed`, err)
        loading.value = false
        loadingMore.value = false
        isLoadingMore = false  // 重置滑动加载标志
      })
    }

    // 加载更多
    function handleLoadMore() {
      if (!hasMore.value || loadingMore.value || loading.value) return
      filterParams.pageNo += 1
      getPoolData(true)
    }

    // 可视区域变化回调：更�?visiblePoolList，用�?WebSocket 只订阅可视区域内的股�?
    // �?500ms 防抖，等滚动停止后再切换订阅，避免滚动中频繁重订阅导致数据来不及显示
    let visibleRangeTimer: ReturnType<typeof setTimeout> | null = null
    function handleVisibleRangeChange(visibleData: any[]) {
      if (visibleRangeTimer) {
        clearTimeout(visibleRangeTimer)
      }
      visibleRangeTimer = setTimeout(() => {
        visibleRangeTimer = null
        visiblePoolList.value = visibleData || []
      }, 500)
    }

    // 筹码大师批量请求函数：最多请�?次（500条），超出部分交给滚动加�?
    async function getChipMasterPoolBatch() {
      // 增加请求序列号，用于处理快速切换时的竞态条�?
      requestSequence++
      const currentSequence = requestSequence

      loading.value = true
      poolList.value = []
      totalCount.value = 0

      const batchPageSize = 100 // 每次请求100条数�?
      const maxBatchPages = 5   // 最多批量请�?�?

      // 构建请求参数
      const signalTypeMap = currentCourseConfig.value.signalTypeMap || { latest: 'upBreak', history: 'strongSupport' }
      const batchFieldMap = getSortFieldMap(currentCourseNo.value)
      const batchApiSortField = filterParams.sortField ? (batchFieldMap[filterParams.sortField] || filterParams.sortField) : 'pxChangeRate'
      const buildQueryData = (pageNo: number) => ({
        trendType: signalTypeMap[signalType.value as 'latest' | 'history'] || 'upBreak',
        optional: filterParams.onlyOptional,
        pageNo: pageNo,
        pageSize: batchPageSize,
        sortField: batchApiSortField,
        sortType: filterParams.sortType || 'desc',
        ...(filterParams.onlyOptional === 1 ? { userName: filterParams.userName || route.query.userName || '' } : {})
      })

      const buildRequestData = (queryData: any) => ({
        data: queryData,
        headers: {
          clienttype: 'pc',
          appVersion: route.query.appVersion,
          token: route.query.token,
          "Content-Type": "application/json"
        }
      })

      try {
        // 第一次请求，获取总数
        const firstRes: any = await stockpool.getChipMasterPool(buildRequestData(buildQueryData(1)))

        // 检查请求序列号
        if (currentSequence !== requestSequence) {
          console.log(`[筹码大师批量请求] 忽略旧请求响应，当前序列�? ${requestSequence}，响应序列号: ${currentSequence}`)
          return
        }

        if (!firstRes.data) {
          loading.value = false
          return
        }

        const total = firstRes.data.total || 0
        totalCount.value = total

        // 处理第一批数据（先存到临时数组，不触发响应式更新�?
        const allData: any[] = (firstRes.data.list || []).map((item: any) => ({
          ...item,
          pxChangRate: item.pxChangeRate
        }))

        // 更新时间
        chipMasterTradingDay.value = firstRes.data.tradingDay || 0
        chipMasterUpdateTime.value = firstRes.data.updateTime || 0
        if (firstRes.data.updateTime) {
          updateTime.value = formatChipMasterUpdateTime(firstRes.data.updateTime, firstRes.data.tradingDay)
        }

        // 计算需要请求的总次数（不多请求一次）
        const totalPages = Math.ceil(total / batchPageSize)
        // 限制最多批量请�?次，超出部分交给滚动加载
        const batchPages = Math.min(totalPages, maxBatchPages)
        console.log(`[筹码大师批量请求] 总数: ${total}, 每页: ${batchPageSize}, 总页�? ${totalPages}, 批量请求页数: ${batchPages}`)

        // 如果只有一页，一次性赋�?
        if (batchPages <= 1) {
          poolList.value = allData
          hasMore.value = allData.length < total
          filterParams.pageNo = 1
          loading.value = false
          return
        }

        // 并发请求剩余页面（最多到�?页）
        const remainingPages = Array.from({ length: batchPages - 1 }, (_, i) => i + 2)
        const remainingRequests = remainingPages.map(pageNo =>
          stockpool.getChipMasterPool(buildRequestData(buildQueryData(pageNo)))
        )

        const remainingResults = await Promise.all(remainingRequests)

        // 再次检查请求序列号
        if (currentSequence !== requestSequence) {
          console.log(`[筹码大师批量请求] 忽略旧请求响应（批量），当前序列�? ${requestSequence}，响应序列号: ${currentSequence}`)
          return
        }

        // 合并所有数据到临时数组
        remainingResults.forEach((res: any) => {
          if (res.data && res.data.list) {
            const mappedList = res.data.list.map((item: any) => ({
              ...item,
              pxChangRate: item.pxChangeRate
            }))
            allData.push.apply(allData, mappedList)
          }
        })

        // 一次性赋值，只触发一次响应式更新和一�?WebSocket 重新订阅
        poolList.value = allData

        // 更新 filterParams.pageNo 以便滚动加载继续
        filterParams.pageNo = batchPages
        // 判断是否还有更多数据需要滚动加�?
        hasMore.value = poolList.value.length < total
        console.log(`[筹码大师批量请求] 完成，共加载 ${poolList.value.length} 条数据，还有更多: ${hasMore.value}`)
      } catch (err) {
        // 检查请求序列号
        if (currentSequence !== requestSequence) {
          console.log(`[筹码大师批量请求] 忽略旧请求错误，当前序列�? ${requestSequence}，响应序列号: ${currentSequence}`)
          return
        }
        console.error('[筹码大师批量请求] 失败:', err)
      } finally {
        if (currentSequence === requestSequence) {
          loading.value = false
        }
      }
    }

    // 信号类型变化（盟主趋势带防抖，防止快速切换导致显示问题）
    async function handleSignalTypeChange(type: string) {
      // 如果切换到相同的类型，直接返�?
      if (signalType.value === type) return

      // 开启切换锁
      isSwitchingTab.value = true

      // 1. 同步保存旧二�?tab 的排序状�?
      if (isDragonSignal.value && (signalType.value === 'latest' || signalType.value === 'history')) {
        const oldKey = getCurrentSortKey()
        allSortStates[oldKey] = { sortField: filterParams.sortField, sortType: filterParams.sortType }
      }

      // 2. 立即更新状态和排序参数
      signalType.value = type
      resetListState()

      if (isDragonSignal.value) {
        // 恢复新二�?tab 的排序状�?
        if (type === 'latest' || type === 'history') {
          const newKey = getCurrentSortKey(undefined, type)
          const savedSort = allSortStates[newKey]
          if (savedSort && savedSort.sortField) {
            filterParams.sortField = savedSort.sortField
            filterParams.sortType = savedSort.sortType
          } else {
            // 默认恢复：涨幅倒序
            filterParams.sortField = 'pxChangRate'
            filterParams.sortType = 'desc'
          }
          if (poolTableRef.value && poolTableRef.value.resetSort) {
            poolTableRef.value.resetSort(filterParams.sortField, filterParams.sortType)
          }
        }
      }

      // 3. 处理数据加载逻辑
      if (isChipMaster.value) {
        getChipMasterPoolBatch()
      } else if (isDragonSignal.value) {
        // 龙头信号：处理特殊的二级 tab 逻辑
        if (type === 'backtest') {
          visiblePoolList.value = []
          isSwitchingTab.value = false
          return
        } else if (type === 'history') {
          if (historyDates.value.length === 0) {
            const currentTimestamp = Math.floor(Date.now() / 1000)
            await fetchTradingDays(currentTimestamp)
          }
          if (poolList.value.length > 0) {
            visiblePoolList.value = poolList.value.slice(0, 20)
          }
        } else {
          filterParams.tradeDay = ''
        }
        getPoolData()
      } else if (isMengzhuTrend.value) {
        // 盟主趋势模式下，添加防抖处理
        // 清除之前的防抖定时器
        if (signalTypeChangeTimer) {
          clearTimeout(signalTypeChangeTimer)
          signalTypeChangeTimer = null
        }

        // 防抖处理数据请求
        signalTypeChangeTimer = setTimeout(async () => {
          signalTypeChangeTimer = null
          if (type === 'history') {
            // 盟主趋势切换到历史信号时，只有在没有历史日期数据时才重新获取
            // 避免来回切换时导致下拉框内容减少
            if (historyDates.value.length === 0) {
              const currentTimestamp = Math.floor(Date.now() / 1000)
              await fetchTradingDays(currentTimestamp)
            }
            // 如果已有历史日期数据，FilterPanel 会自动选择第一个日期并触发 date-change
          } else {
            filterParams.tradeDay = ''
            resetListState()
            getPoolData()
          }
        }, SIGNAL_TYPE_CHANGE_DELAY)
      } else if (type === 'history') {
        // 四维猎手等其他产品，保持原有逻辑
        const currentTimestamp = Math.floor(Date.now() / 1000)
        await fetchTradingDays(currentTimestamp)
      } else {
        filterParams.tradeDay = ''
        resetListState()
        getPoolData()
      }
      // 延迟释放�?
      setTimeout(() => { isSwitchingTab.value = false }, 100)
    }

    // 盟主趋势顶级 Tab 切换（带防抖，防止快速切换导致显示问题）
    function handleMainTabChange(tab: string) {
      // 开启切换锁
      isSwitchingTab.value = true

      // 龙头猎手一�?tab 切换
      if (isDragonHunter.value) {
        if (dragonHunterMainTab.value === tab) {
          isSwitchingTab.value = false
          return
        }

        // 1. 保存�?tab 的排序状�?
        const oldKey = getCurrentSortKey()
        allSortStates[oldKey] = { sortField: filterParams.sortField, sortType: filterParams.sortType }

        dragonHunterMainTab.value = tab
        // 切换一�?tab 时重置二�?tab 到第一�?
        dragonHunterSubTab.value = 'dragonStar'
        // 离开查看往期时清空搜索状�?
        if (tab !== 'beforeChose') {
          dragonHunterSelectedStock.value = null
          dragonHunterSearchSymbol.value = ''
          dragonHunterSearchMarket.value = ''
        }
        // Bug2：切�?tab 时重置只看自选，避免切换后列表仍显示自选数�?
        dragonHunterOnlyUser.value = 0

        // 数据追踪/热门案例/查看往期：清空订阅，停�?WebSocket
        if (tab === 'dataTrack' || tab === 'hotCase' || tab === 'beforeChose') {
          visiblePoolList.value = []
          if (tab === 'dataTrack') {
            isSwitchingTab.value = false
            return
          }
        }
        loading.value = true
        poolList.value = []
        totalCount.value = 0
        filterParams.pageNo = 1
        hasMore.value = true

        // 2. 恢复新 tab 的排序状态（默认重置为第一个子 tab）
        const newKey = getCurrentSortKey(tab, 'dragonStar')
        const savedSort = allSortStates[newKey]
        if (savedSort && savedSort.sortField) {
          filterParams.sortField = savedSort.sortField
          filterParams.sortType = savedSort.sortType
        } else {
          // 热门案例默认按 5日区间最高涨幅降序，查看往期默认按入选时间降序，其他默认涨幅倒序
          if (tab === 'hotCase') {
            filterParams.sortField = 'day5Rate'
            filterParams.sortType = 'desc'
          } else if (tab === 'beforeChose') {
            filterParams.sortField = 'hunterDateTime'
            filterParams.sortType = 'desc'
          } else {
            filterParams.sortField = 'pxChangRate'
            filterParams.sortType = 'desc'
          }
        }
        if (poolTableRef.value && poolTableRef.value.resetSort) {
          poolTableRef.value.resetSort(filterParams.sortField, filterParams.sortType)
        }

        getPoolData()
        // 延迟释放�?
        setTimeout(() => { isSwitchingTab.value = false }, 100)
        return
      }

      // 如果切换到相同的 Tab，直接返�?
      if (mengzhuMainTab.value === tab) {
        isSwitchingTab.value = false
        return
      }

      // 1. 同步保存�?tab 的排序状�?
      const oldKey = getCurrentSortKey()
      allSortStates[oldKey] = { sortField: filterParams.sortField, sortType: filterParams.sortType }

      // 2. 立即更新 Tab 状态和排序参数
      mengzhuMainTab.value = tab
      filterParams.pageNo = 1
      hasMore.value = true

      if (isDragonSignal.value) {
        // 龙头信号切换：立即恢复新 tab 的独立排序状态（切换一级菜单后二级菜单默认重置�?latest�?
        signalType.value = 'latest'
        filterParams.tradeDay = ''
        const newKey = getCurrentSortKey(tab, 'latest')
        const savedSort = allSortStates[newKey]
        if (savedSort && savedSort.sortField) {
          filterParams.sortField = savedSort.sortField
          filterParams.sortType = savedSort.sortType
        } else {
          // 默认恢复：涨幅倒序
          filterParams.sortField = 'pxChangRate'
          filterParams.sortType = 'desc'
        }
        if (poolTableRef.value && poolTableRef.value.resetSort) {
          poolTableRef.value.resetSort(filterParams.sortField, filterParams.sortType)
        }
      } else if (tab !== 'hotCases') {
        // 盟主趋势切换到信号池：恢复默认排序
        filterParams.sortField = 'pxChangRate'
        filterParams.sortType = 'desc'
        if (poolTableRef.value && poolTableRef.value.resetSort) {
          poolTableRef.value.resetSort(filterParams.sortField, filterParams.sortType)
        }
      } else {
        // 盟主趋势切换到热门案�?
        filterParams.sortField = ''
        filterParams.sortType = ''
        if (poolTableRef.value && poolTableRef.value.resetSort) {
          poolTableRef.value.resetSort('', '')
        }
      }

      // 3. 处理数据加载逻辑（保持防抖，仅限数据请求部分�?
      loading.value = true
      poolList.value = []
      totalCount.value = 0

      if (mainTabChangeTimer) {
        clearTimeout(mainTabChangeTimer)
        mainTabChangeTimer = null
      }

      mainTabChangeTimer = setTimeout(() => {
        mainTabChangeTimer = null
        if (isDragonSignal.value) {
          getPoolData()
        } else if (tab === 'hotCases') {
          getMengzhuHotCases()
        } else {
          getPoolData()
        }
        // 延迟释放�?
        setTimeout(() => { isSwitchingTab.value = false }, 100)
      }, MAIN_TAB_CHANGE_DELAY)
    }

    // 龙头猎手二级 tab 切换
    function handleDragonHunterSubTabChange(subTab: string) {
      if (dragonHunterSubTab.value === subTab) return

      // 开启切换锁
      isSwitchingTab.value = true

      // 1. 保存旧二�?tab 的排序状�?
      const oldKey = getCurrentSortKey()
      allSortStates[oldKey] = { sortField: filterParams.sortField, sortType: filterParams.sortType }

      dragonHunterSubTab.value = subTab
      // 数据追踪不请求股池数据，由组件内部处�?
      if (dragonHunterMainTab.value === 'dataTrack') {
        isSwitchingTab.value = false
        return
      }
      // 切换二级 tab 时先清空订阅，等新数据加载完再恢�?
      visiblePoolList.value = []
      loading.value = true
      poolList.value = []
      totalCount.value = 0
      filterParams.pageNo = 1
      hasMore.value = true

      // 2. 恢复新二�?tab 的排序状�?
      if (dragonHunterMainTab.value === 'newChose') {
        const newKey = getCurrentSortKey(undefined, subTab)
        const savedSort = allSortStates[newKey]
        if (savedSort && savedSort.sortField) {
          filterParams.sortField = savedSort.sortField
          filterParams.sortType = savedSort.sortType
        } else {
          // 默认恢复：涨幅倒序
          filterParams.sortField = 'pxChangRate'
          filterParams.sortType = 'desc'
        }
        if (poolTableRef.value && poolTableRef.value.resetSort) {
          poolTableRef.value.resetSort(filterParams.sortField, filterParams.sortType)
        }
      }

      getPoolData()
      // 延迟释放�?
      setTimeout(() => { isSwitchingTab.value = false }, 100)
    }

    // 龙头猎手往期入选：只看自选切�?
    function handleDragonHunterOptionalChange(val: boolean) {
      dragonHunterOnlyUser.value = val ? 1 : 0
      if (val) {
        dragonHunterSearchSymbol.value = ''
        dragonHunterSearchMarket.value = ''
      }
      visiblePoolList.value = []
      resetListState()
      getPoolData()
    }

    // 龙头猎手往期入选：搜索个股
    function handleDragonHunterStockSearch(item: any) {
      dragonHunterSearchSymbol.value = item.Code || item.symbol || ''
      dragonHunterSearchMarket.value = (item.MarketCode || item.market || '').toLowerCase()
      dragonHunterOnlyUser.value = 0
      visiblePoolList.value = []
      resetListState()
      getPoolData()
    }

    // 龙头猎手往期入选：表格行点击，回填搜索�?
    function handlePoolTableRowClick(record: any) {
      if (!isDragonHunter.value || dragonHunterMainTab.value !== 'beforeChose') return
      dragonHunterSelectedStock.value = record
      dragonHunterSearchSymbol.value = record.symbol || ''
      dragonHunterSearchMarket.value = (record.market || '').toLowerCase()
      dragonHunterOnlyUser.value = 0
      visiblePoolList.value = []
      resetListState()
      getPoolData()
    }

    // 龙头猎手往期入选：清空搜索�?
    function handleDragonHunterSearchClear() {
      dragonHunterSelectedStock.value = null
      dragonHunterSearchSymbol.value = ''
      dragonHunterSearchMarket.value = ''
      visiblePoolList.value = []
      resetListState()
      getPoolData()
    }

    // 获取盟主趋势�?0天热门案例数�?
    function getMengzhuHotCases() {
      // 增加请求序列号，用于处理快速切换时的竞态条�?
      requestSequence++
      const currentSequence = requestSequence

      loading.value = true

      const data: any = {}
      data.data = {}
      data.headers = {
        clienttype: 'pc',
        appVersion: route.query.appVersion,
        token: route.query.token,
        "Content-Type": "application/json"
      }

      stockpool.getMengzhuTrendCard(data).then((res: any) => {
        // 检查请求序列号，如果不是最新请求则忽略响应（解决快速切换时的竞态条件）
        if (currentSequence !== requestSequence) {
          console.log(`[盟主趋势] 忽略旧的热门案例请求响应，当前序列号: ${requestSequence}，响应序列号: ${currentSequence}`)
          return
        }

        // 再次检查当�?Tab 是否仍然�?hotCases，避免数据错�?
        if (mengzhuMainTab.value !== 'hotCases') {
          console.log(`[盟主趋势] 当前 Tab 已切换为 ${mengzhuMainTab.value}，忽略热门案例响应`)
          loading.value = false
          return
        }

        console.log('getMengzhuTrendCard res:', res)
        if (res.data) {
          // 数据直接�?res.data 数组中，不是 res.data.cardList
          const rawList = Array.isArray(res.data) ? res.data : (res.data.cardList || res.data || [])
          const newData = rawList.map((item: any, index: number) => {
            // �?symbol 中提�?market（如果没�?market 字段�?
            // symbol 格式可能�?"600000" �?"sh600000"
            let market = item.market || ''
            if (!market && item.symbol) {
              // 根据股票代码判断市场
              const code = item.symbol
              if (code.startsWith('6') || code.startsWith('5')) {
                market = 'sh'
              } else if (code.startsWith('0') || code.startsWith('3') || code.startsWith('1')) {
                market = 'sz'
              } else if (code.startsWith('4') || code.startsWith('8')) {
                market = 'bj'
              }
            }
            return {
              ...item,
              market: market,
              index: index + 1,
              // 字段映射：保留原始的 pxChangeRate 作为涨幅，cumRate 作为累计涨幅
              pxChangRate: item.pxChangeRate || 0 // 涨幅（通过 WebSocket 实时更新�?
            }
          })
          poolList.value = newData
          totalCount.value = rawList.length
          hasMore.value = false // �?0天热门案例不分页

          // 打印第一条数据，用于调试 WebSocket 匹配
          if (newData.length > 0) {
            console.log('[盟主趋势热门案例] 第一条数�?', JSON.stringify(newData[0], null, 2))
          }

          // 更新时间（如果有�?
          if (res.data.updateTime) {
            updateTime.value = formatMengzhuUpdateTime(res.data.updateTime)
          }
        }
        loading.value = false
      }).catch((err: any) => {
        // 检查请求序列号，如果不是最新请求则忽略错误处理
        if (currentSequence !== requestSequence) {
          console.log(`[盟主趋势] 忽略旧的热门案例请求错误，当前序列号: ${requestSequence}，响应序列号: ${currentSequence}`)
          return
        }
        console.error('getMengzhuTrendCard failed', err)
        loading.value = false
      })
    }

    function deduplicateStocks(existingList: any[], newList: any[]): any[] {
      if (!newList || newList.length === 0) {
        return []
      }
      return newList.filter((item: any) => item != null)
    }

    // 重置列表状�?
    function resetListState() {
      filterParams.pageNo = 1
      poolList.value = [] as any
      hasMore.value = true
    }

    // 刷新 - 默认按涨幅倒序排序
    function handleRefresh() {
      resetListState()
      // 刷新时先清空 visiblePoolList，强制触发 websocket2 取消订阅
      // 避免新数据与旧数据相同时签名不变导致不重新订阅（成交额无法更新）
      visiblePoolList.value = []
      // 设置默认排序：涨幅倒序
      filterParams.sortField = 'pxChangRate'
      filterParams.sortType = 'desc'
      // 重置表格排序箭头显示
      if (poolTableRef.value && poolTableRef.value.resetSort) {
        poolTableRef.value.resetSort()
      }
      getPoolData()
    }

    // 筛选变�?
    function handleFilterChange(filterData: { qs: number; zj: number; tc: string }) {
      filterParams.qs = filterData.qs
      filterParams.zj = filterData.zj
      filterParams.tc = filterData.tc
      // 判断是否有筛选条�?
      filterParams.isChose = (filterData.qs !== 0 || filterData.zj !== 0 || filterData.tc !== '0') ? 1 : 0
      resetListState()
      getPoolData()
    }

    // 日期变化
    function handleDateChange(date: string) {
      const timestamp = moment(date).valueOf()
      filterParams.tradeDay = String(timestamp)
      resetListState()
      getPoolData()
    }

    // 自选变�?
    function handleOptionalChange(value: boolean) {
      filterParams.onlyOptional = value ? 1 : 0
      resetListState()
      getPoolData()
    }

    // 表格变化（排序）- 使用配置化的排序字段映射
    function handleTableChange({ sorter }: any) {
      // 正在切换 Tab 时不处理表格变化（防止由于子组件重置导致的错误保存）
      if (isSwitchingTab.value) return

      if (sorter && sorter.field) {
        // filterParams.sortField 存前端列 key，发请求时再通过 fieldMap 转成接口字段�?
        filterParams.sortField = sorter.field
        filterParams.sortType = sorter.order === 'ascend' ? 'asc' : 'desc'
      } else {
        // 取消排序时清空排序参�?
        filterParams.sortField = ''
        filterParams.sortType = ''
      }

      // 手动排序时同步保存到一二级组合的独立排序状�?
      if (isDragonHunter.value) {
        const key = getCurrentSortKey()
        allSortStates[key] = { sortField: filterParams.sortField, sortType: filterParams.sortType }
      } else if (isDragonSignal.value) {
        const key = getCurrentSortKey()
        allSortStates[key] = { sortField: filterParams.sortField, sortType: filterParams.sortType }
      }

      // Bug2修复：龙头猎手最新入选（newChose）接口不支持服务端排序，
      // 直接在前端对已有数据排序，不重新请求接口，避免中�?WebSocket 订阅链路
      if (isDragonHunter.value && dragonHunterMainTab.value === 'newChose') {
        sortPoolList()
        return
      }

      // beforeChose 排序时重新请求，需清空 visiblePoolList 以触�?watch(poolList) 的初始订�?
      if (isDragonHunter.value && dragonHunterMainTab.value === 'beforeChose') {
        visiblePoolList.value = []
      }

      resetListState()
      getPoolData()
    }

    // 打开AI解读浮层
    function handleOpenAiInterp(record: any) {
      if (aiInterpModalRef.value) {
        aiInterpModalRef.value.open(record)
      }
    }

    // 股票单击选择（筹码大师搜索功能）
    // isClick=1 弹窗模式：单击不执行任何操作（只有双击才有效�?
    // isClick=2 内嵌模式：单击执行设置焦点股�?
    function handleStockSelect(item: any) {
      // isClick=1 弹窗模式：单击不执行任何操作
      if (isClick.value === 1) {
        console.log('筹码大师搜索单击（弹窗模式，不执行）')
        return
      }

      // isClick=2 内嵌模式：执行单击交互（设置焦点股票�?
      const code = item.Code
      const market = (item.MarketCode || 'sz').toLowerCase()
      const clickUrl = `gw://funccall?funcname=OnFireStyleEvent;Command=set_focus_stock;param=<params><code>${code}</code><market_letter>${market}</market_letter></params>`
      window.open(clickUrl)
      console.log('筹码大师搜索单击:', clickUrl)
    }

    // 股票双击选择（筹码大师搜索功能）
    // isClick=1 �?isClick=2 都执行双击交互（设置股票列表、打开K线图、显示指标）
    function handleStockDblClick(item: any) {
      const code = item.Code
      const market = (item.MarketCode || 'sz').toLowerCase()

      setTimeout(() => {
        // 构建股票列表：将选中的股票放到最前面，其余股票按原顺序排�?
        const selectedSymbol = code
        const clickedIndex = poolList.value.findIndex((listItem: any) => listItem.symbol === selectedSymbol)

        let stockList = ''
        if (clickedIndex >= 0) {
          // 如果选中的股票在列表中，重新排序：选中的放最前面
          const afterClicked = poolList.value.slice(clickedIndex + 1)
          const beforeClicked = poolList.value.slice(0, clickedIndex)
          const reorderedList = [poolList.value[clickedIndex]].concat(afterClicked).concat(beforeClicked)
          stockList = reorderedList.map((listItem: any) => `${listItem.market || 'sz'}:${listItem.symbol}`).join(',')
        } else {
          // 如果选中的股票不在列表中（搜索到的新股票），将其放到最前面
          const existingList = poolList.value.map((listItem: any) => `${listItem.market || 'sz'}:${listItem.symbol}`).join(',')
          stockList = existingList ? `${market}:${code},${existingList}` : `${market}:${code}`
        }

        // 第一个调用：设置股票列表
        const url1 = `gw://funccall?funcname=SetCurStockList;list=${stockList}`
        window.open(url1)
        console.log('筹码大师搜索双击 url1:', url1)

        // 第二个调用：打开K线图
        setTimeout(() => {
          const url2 = `gw://funccall?funcname=OnFireCommand;setcurrent=1;Command=kline;param=<params><kline_special_event>0</kline_special_event><StyleName>JSFX</StyleName><code>${code}</code><market_letter>${market}</market_letter><periodstr>DAY</periodstr><TabName>K线图</TabName><flag_container_style>K</flag_container_style></params>`
          window.open(url2)
          console.log('筹码大师搜索双击 url2:', url2)

          // 第三个调用：显示指标
          setTimeout(() => {
            const indAlias = (productData.value && productData.value[0]?.poolName) || currentCourseConfig.value.courseName
            const url3 = `gw://funccall?funcname=OnFireCommand;Command=yin_ind_show;param=<params><ind_alias>${indAlias}</ind_alias><ingnore_paras>${1}</ingnore_paras></params>`
            window.open(url3)
            console.log('筹码大师搜索双击 url3:', url3)
          }, 100)
        }, 100)
      }, 50)
    }

    // 标记当前是否正在执行滑动加载（isLoadMore=true 的请求），用�?watch(poolList) 判断
    // 避免依赖 loadingMore 的时序状态（loadingMore �?.then() 里才�?false�?
    // �?poolList 赋值在 .then() 里更早触�?watch，此�?loadingMore 仍为 true�?
    let isLoadingMore = false

    // 监听 poolList 变化，数据加载完成后立即初始�?WebSocket 订阅
    // IntersectionObserver 异步回调不可靠，直接赋值确保订阅立即生�?
    // 后续 IntersectionObserver 稳定后会通过 handleVisibleRangeChange 更新为真实可见行
    watch(poolList, (newList) => {
      // dataTrack 不需要订�?
      if (isDragonHunter.value && dragonHunterMainTab.value === 'dataTrack') return
      // beforeChose（查看往期）：可视区域完全由 IntersectionObserver 通过 handleVisibleRangeChange 维护�?
      // 此处只在首次加载（visiblePoolList 为空）时做一次兜底初始化�?
      // 避免 IntersectionObserver 回调延迟期间无任何订阅；
      // 滑动加载追加数据时（isLoadingMore=true）也跳过，防止覆盖已有订阅�?
      if (isDragonHunter.value && dragonHunterMainTab.value === 'beforeChose') {
        if (isLoadingMore) return
        // 仅在首次加载（列表从空变为有数据）时做初始订阅，后续�?IntersectionObserver 接管
        if (visiblePoolList.value.length === 0 && newList.length > 0) {
          setTimeout(() => {
            visiblePoolList.value = newList.slice(0, 40)
          }, 250)
        }
        return
      }
      if (newList.length > 0) {
        // 延迟赋值，确保 websocket2 的防�?suboff�?00ms）已执行完毕�?
        // 避免 subon �?suboff 竞争导致订阅失效
        setTimeout(() => {
          visiblePoolList.value = newList.slice(0, 40)
        }, 250)
      }
    })

    // 初始化加�?
    onMounted(async () => {
      // 先获取产品数据，确保 historyDays 等配置可�?
      await fetchProductData()

      // 设置默认排序：涨幅倒序（存前端�?key，发请求时再转成接口字段名）
      filterParams.sortField = 'pxChangRate'
      filterParams.sortType = 'desc'

      // 筹码大师使用批量请求逻辑，其他产品使用普通请�?
      if (isChipMaster.value) {
        getChipMasterPoolBatch()
      } else {
        getPoolData()
      }

      // 初始化时也获取交易日列表（使用当前时间戳�?
      const currentTimestamp = Math.floor(Date.now() / 1000)
      await fetchTradingDays(currentTimestamp)
    })

    // 排序字段映射：接口字段名 -> 前端数据字段�?
    const sortFieldToDataKey: Record<string, string> = {
      // 四维猎手字段映射
      'ClosePx': 'closePx',
      'LastPx': 'lastPx',
      'PxChangRate': 'pxChangRate',
      'HighRate': 'highRate',
      'Macd': 'macd',
      'AmountDif': 'amount',
      'PlateType': 'plateType',
      'Volume': 'volume',
      'Turnover': 'turnover',
      'TurnoverRate': 'turnoverRate',
      // 箱体擒龙字段映射
      'lastPx': 'lastPx', 
      'pxChangeRate': 'pxChangRate',
      'highPct': 'highPct',
      'businessBalance': 'businessBalance',
      'turnoverRatio': 'turnoverRatio',
      'selectedPrice': 'selectedPrice',
      // 筹码大师字段映射
      'state': 'stateColor',
      // 盟主趋势字段映射
      'PxChangeRate': 'pxChangRate',
      'BuyPx': 'buyPx',
      'MaxRate': 'maxRate',
      'CumRate': 'cumRate',
      'Amount': 'amount',
      'FundStatus': 'fundStatus',
      'SellTime': 'sellTime',
      // 龙头信号字段映射
      'stockHot': 'stockHot',
      // 龙头猎手最新入选字段映射（前端本地排序用，lastPx/pxChangRate/amount 已在上方定义）
      'pxChangRate': 'pxChangRate',
      'price': 'price',
      'symbolHot': 'symbolHot',
      'plateHot': 'plateHot',
      'plateName': 'plateName',
      'amount': 'amount',
      // 龙头猎手热门案例/往期入选字段映射
      'day5Rate': 'day5Rate',
      'hunterDateTime': 'dateTime',
      'hunterSelectType': 'selectType',
 
    }

    // 根据当前排序条件对列表进行排�?
    function sortPoolList() {
      if (!filterParams.sortField || !filterParams.sortType) return

      const dataKey = sortFieldToDataKey[filterParams.sortField]
      if (!dataKey) return

      const isAsc = filterParams.sortType === 'asc'

      poolList.value.sort((a: any, b: any) => {
        const rawA = a[dataKey]
        const rawB = b[dataKey]

        // null/undefined 值始终排在最后（不管升序还是降序�?
        const aIsNull = rawA === null || rawA === undefined
        const bIsNull = rawB === null || rawB === undefined
        if (aIsNull && bIsNull) return 0
        if (aIsNull) return 1   // a �?null，排�?b 后面
        if (bIsNull) return -1  // b �?null，排�?a 后面

        const valA = rawA
        const valB = rawB

        // 盟主资金(fundStatus)特殊处理：值越小越好（1=多头强势最好，4=空头偏强最差）
        // 所以降序时应该是小的在前面，升序时大的在前面（与普通数值排序相反）
        if (dataKey === 'fundStatus') {
          if (isAsc) {
            return valB - valA  // 升序：大的在前（差的在前�?
          } else {
            return valA - valB  // 降序：小的在前（好的在前�?
          }
        }

        // 四维猎手题材追踪(plateType)特殊处理：核�?1) > 普�?2) > 未入�?0)
        if (dataKey === 'plateType') {
          const getPriority = (v: any) => {
            const s = String(v)
            if (s === '1') return 1 // 核心
            if (s === '2') return 2 // 普�?
            if (s === '0') return 3 // 未入�?
            return 99
          }
          const priA = getPriority(valA)
          const priB = getPriority(valB)
          if (isAsc) {
            return priB - priA
          } else {
            return priA - priB
          }
        }

        // 其他字段正常排序
        if (typeof valA === 'string' || typeof valB === 'string') {
          // 字符串字段按字典序排序（�?plateName 所属板块）
          const strA = String(valA)
          const strB = String(valB)
          if (isAsc) {
            return strA.localeCompare(strB, 'zh-CN')
          } else {
            return strB.localeCompare(strA, 'zh-CN')
          }
        }
        if (isAsc) {
          return valA - valB
        } else {
          return valB - valA
        }
      })
    }

    function websocketonmessage(event: any) {
      // 更新列表数据（列表订�?onHQLIST 格式�?
      if (poolList.value && poolList.value.length) {
        try {
          const wsData = JSON.parse(event.data)
          console.log('WebSocket收到列表数据:', wsData)

          // 列表订阅返回的数据在 QuoteData.DynaData 数组�?
          const dynaDataList = wsData?.QuoteData?.DynaData || []

          if (dynaDataList.length) {
            // 打印第一条数据的完整结构，用于调�?
            console.log('第一条推送数据结�?', JSON.stringify(dynaDataList[0], null, 2))

            // 判断是否为箱体擒�?
            const isBoxDragon = currentCourseNo.value === '33130259066e4650bd0b7cf6b23d9488'
            // 判断是否为盟主趋�?
            const isMengzhuTrendProduct = currentCourseNo.value === '399a9139a99a4300a9bf004b9949eb9f'
            // 判断是否为龙头信�?
            const isDragonSignalProduct = currentCourseNo.value === 'b20f2d6e654948ae8c99bf650cbbb0e2'
            // 判断是否为龙头猎�?
            const isDragonHunterProduct = currentCourseNo.value === '03fc903a5ac84ff6982e4b83a1f35c61'

            // 盟主趋势�?0天热门案例调试日�?
            if (isMengzhuTrendProduct && mengzhuMainTab.value === 'hotCases' && poolList.value.length > 0) {
              console.log('[盟主趋势热门案例] poolList第一条数�?', {
                symbol: poolList.value[0].symbol,
                market: poolList.value[0].market,
                lastPx: poolList.value[0].lastPx,
                pxChangRate: poolList.value[0].pxChangRate
              })
              console.log('[盟主趋势热门案例] WebSocket第一条数�?', {
                InstrumentID: dynaDataList[0].InstrumentID,
                Inst: dynaDataList[0].Inst,
                ExchangeID: dynaDataList[0].ExchangeID,
                Market: dynaDataList[0].Market,
                LastPrice: dynaDataList[0].LastPrice,
                UpdownRate: dynaDataList[0].UpdownRate,
                Amount: dynaDataList[0].Amount
              })
            }

            // 市场代码标准化函数：将各种格式的市场代码统一为小写的 sh/sz/bj
            const normalizeMarket = (market: string): string => {
              const m = (market || '').toLowerCase()
              // 处理各种可能的市场代码格�?
              if (m === 'sh' || m === 'sse' || m === '1' || m === 'sha' || m === 'shetf') return 'sh'
              if (m === 'sz' || m === 'szse' || m === '0' || m === 'sza' || m === 'szetf') return 'sz'
              if (m === 'bj' || m === 'bse' || m === '2') return 'bj'
              return m
            }

            // 股票代码标准化：统一转为字符串，并补零到6位（兼容接口返回数字类型�?symbol�?
            const normalizeSymbol = (symbol: any): string => {
              if (symbol === null || symbol === undefined) return ''
              const s = String(symbol)
              // 补零�?位（A股代码均�?位）
              return s.padStart(6, '0')
            }

            // 构建 WebSocket 推送数据的 Map，用标准化后�?symbol 作为 key 实现 O(1) 查找
            const wsDataMap = new Map<string, any>()
            dynaDataList.forEach((msgItem: any) => {
              const msgInst = msgItem.InstrumentID || msgItem.Inst
              if (msgInst) {
                // 同时存储原始 key 和标准化 key，确保各种格式都能命�?
                wsDataMap.set(String(msgInst), msgItem)
                const normalized = normalizeSymbol(msgInst)
                if (normalized !== String(msgInst)) {
                  wsDataMap.set(normalized, msgItem)
                }
              }
            })

            // 标记是否有数据变化（用于决定是否需要重新排序）
            let hasChanges = false

            poolList.value.forEach((item: any) => {
              // �?item.symbol 做标准化处理，兼容接口返回数字类型或缺少前导零的情况
              const itemSymbolStr = normalizeSymbol(item.symbol)
              const msgItem = wsDataMap.get(itemSymbolStr) || wsDataMap.get(String(item.symbol))
              if (!msgItem) return

              // 验证市场匹配
              const itemMarket = normalizeMarket(item.market || '')
              let matchSuccess = false

              if (itemMarket) {
                const msgMarketRaw = msgItem.ExchangeID || msgItem.Market || ''
                const msgMarket = normalizeMarket(msgMarketRaw)

                // 如果消息中没有市场信息，或者市场信息匹配，都认为是成功�?
                if (!msgMarket || itemMarket === msgMarket) {
                  matchSuccess = true
                } else {
                  // 调试日志：匹配失�?
                  if (item.symbol === '501026') {
                    console.log(`[调试-匹配失败] symbol: ${item.symbol}, itemMarket: ${itemMarket} (原�?${item.market}), msgMarket: ${msgMarket} (原�?${msgMarketRaw})`)
                  }
                  return
                }
              } else {
                matchSuccess = true
              }

              // 盟主趋势热门案例调试日志
              if (isMengzhuTrendProduct && mengzhuMainTab.value === 'hotCases') {
                const itemIndex = poolList.value.indexOf(item)
                if (itemIndex < 3) {
                  console.log(`[盟主趋势热门案例] 匹配成功 #${itemIndex}:`, {
                    itemSymbol: item.symbol,
                    msgInst: msgItem.InstrumentID || msgItem.Inst,
                    LastPrice: msgItem.LastPrice,
                    UpdownRate: msgItem.UpdownRate
                  })
                }
              }



              hasChanges = true

              // 更新最新价
              if (msgItem.LastPrice !== undefined && msgItem.LastPrice !== null) {
                item.lastPx = Number(msgItem.LastPrice)
              }

              // 更新涨幅（注意：ETF数据的涨幅可能在不同字段，需兼容处理�?
              // UpdownRate 是小数形式，�?0.05 表示 5%
              if (msgItem.UpdownRate !== undefined && msgItem.UpdownRate !== null) {
                item.pxChangRate = Number(msgItem.UpdownRate)
              } else if (msgItem.Updown !== undefined && msgItem.Updown !== null) {
                item.pxChangRate = Number(msgItem.Updown)
              }

              // 同步更新 PreClosePrice（如果有的），防止其他地方用旧昨收重新计算导致涨幅显示错�?
              if (msgItem.PreClosePrice !== undefined && msgItem.PreClosePrice !== null) {
                item.preClosePx = Number(msgItem.PreClosePrice)
              }



              // 更新成交�?

              if (msgItem.Volume !== undefined && msgItem.Volume !== null) {
                item.volume = msgItem.Volume
              }

              // 更新成交�?
              if (msgItem.Amount !== undefined && msgItem.Amount !== null) {
                if (isBoxDragon) {
                  // 箱体擒龙使用 businessBalance 字段
                  item.businessBalance = msgItem.Amount
                } else if (isMengzhuTrendProduct || isDragonSignalProduct || isDragonHunterProduct) {
                  // 盟主趋势/龙头信号/龙头猎手使用 amount 字段
                  item.amount = msgItem.Amount
                } else {
                  // 四维猎手使用 turnover 字段
                  item.turnover = msgItem.Amount
                }
              }

              // 更新换手率（箱体擒龙特有�?
              if (isBoxDragon && msgItem.TurnoverRate !== undefined && msgItem.TurnoverRate !== null) {
                item.turnoverRatio = msgItem.TurnoverRate
              }
            })

            // 如果有排序条件且有数据变化，更新数据后重新排�?
            // 注意：盟主趋势的�?0天热门案例不需要排序，保持接口返回的顺�?
            // 注意：四维猎手题材追踪(plateName/PlateType)由接口排序返回，前端不重排
            const isPlateTypeSort = filterParams.sortField === 'plateName'
            if (hasChanges && filterParams.sortField && filterParams.sortType && !isPlateTypeSort && !(isMengzhuTrendProduct && mengzhuMainTab.value === 'hotCases')) {
              sortPoolList()
            }
          }
        } catch (e) {
          console.error('websocketonmessage parse error:', e)
        }
      }
    }

    return {
      // ==================== 组件引用 ====================
      poolTableRef,
      aiInterpModalRef,

      // ==================== 基础数据 ====================
      productData,
      activeTab,
      poolList,
      visiblePoolList,
      loading,
      updateTime,
      historyDates,
      hasMore,
      loadingMore,
      theme,
      totalCount,

      // ==================== 课程/产品配置 ====================
      currentCourseNo,
      currentCourseConfig,
      currentWsListKey,
      isClick,
      isChipMaster,
      isMengzhuTrend,
      isDragonHunter,
      mengzhuMainTab,
      dragonHunterMainTab,
      dragonHunterSubTab,

      // ==================== 计算属性（简化模板） ====================
      hasProductData,
      getProductCardProps,
      filterPanelProps,
      poolTableProps,

      // ==================== 事件处理方法 ====================
      fresh,
      websocketonmessage,
      handleFilterChange,
      handleDateChange,
      handleOptionalChange,
      handleTableChange,
      handleSignalTypeChange,
      handleRefresh,
      handleLoadMore,
      handleVisibleRangeChange,
      handleStockSelect,
      handleStockDblClick,
      handleMainTabChange,
      handleOpenAiInterp,
      getMengzhuHotCases,
      handleDragonHunterSubTabChange,
      handleDragonHunterOptionalChange,
      handleDragonHunterStockSearch,
      handlePoolTableRowClick,
      handleDragonHunterSearchClear
    }
  }
})
